<script setup lang="ts">
/**
 * 耿村斗大桥 GLB 模型 + 点云 3D Tiles（pnts）+ 桥面监测点位（vue-cesium / Cesium 全局由 Viewer 注入）
 */
import { onUnmounted, watch } from 'vue'

export type GengcundouBridgePoint = {
    id: string
    name: string
    lng: number
    lat: number
    height: number
}

const props = withDefaults(
    defineProps<{
        /** Cesium Viewer，用于加载 3D Tiles 点云 */
        viewer?: any
        /** Viewer 已就绪后再计算 orientation，避免首屏 Cesium 未注入 */
        viewerReady?: boolean
        /** 3D Tiles 入口（含 pnts 的 tileset.json），默认项目内 public 点云 */
        pointCloudTilesetUrl?: string
    }>(),
    {
        viewerReady: false,
        // 开发环境走 Vite 代理 /pnts，避免跨域；生产环境直连同源资源
        pointCloudTilesetUrl: import.meta.env.DEV
            ? '/pnts/gddq/tileset.json'
            : 'https://fh.zxhkuav.cn/pnts/gddq/tileset.json',
    }
)

const BRIDGE_LNG = 119.91018838410844
const BRIDGE_LAT = 30.968699997112015
const BRIDGE_H = 20
const MODEL_URI = '/219.耿村斗大桥.glb'
const MODEL_MIN_PIXEL = 80

/** 绕竖直轴朝向（度），与原先 viewer 中保持一致 */
const BRIDGE_HEADING_DEG = 6

const bridgeOrientation = () => {
    const CesiumGlobal = (globalThis as any).Cesium
    if (!CesiumGlobal || !props.viewerReady) return undefined
    const position = CesiumGlobal.Cartesian3.fromDegrees(BRIDGE_LNG, BRIDGE_LAT, BRIDGE_H)
    const heading = CesiumGlobal.Math.toRadians(BRIDGE_HEADING_DEG)
    const pitch = CesiumGlobal.Math.toRadians(0)
    const roll = CesiumGlobal.Math.toRadians(0)
    const hpr = new CesiumGlobal.HeadingPitchRoll(heading, pitch, roll)
    return CesiumGlobal.Transforms.headingPitchRollQuaternion(position, hpr)
}

/** 点云 3D Tiles（pnts 由 tileset.json 引用） */
let pointCloudTileset: any
let pointCloudLoadToken = 0

const removePointCloudTileset = () => {
    const v = props.viewer
    if (pointCloudTileset) {
        try {
            if (v?.scene?.primitives?.contains?.(pointCloudTileset)) {
                v.scene.primitives.remove(pointCloudTileset)
            }
            if (!pointCloudTileset.isDestroyed?.()) {
                pointCloudTileset.destroy()
            }
        } catch {
            /* ignore */
        }
        pointCloudTileset = undefined
    }
}

watch(
    () => [props.viewer, props.viewerReady, props.pointCloudTilesetUrl] as const,
    async () => {
        const token = ++pointCloudLoadToken
        removePointCloudTileset()
        if (!props.viewer || !props.viewerReady || !props.pointCloudTilesetUrl) return

        const CesiumGlobal = (globalThis as any).Cesium
        if (!CesiumGlobal?.Cesium3DTileset?.fromUrl) return

        try {
            const tileset = await CesiumGlobal.Cesium3DTileset.fromUrl(props.pointCloudTilesetUrl)
            if (token !== pointCloudLoadToken) {
                if (!tileset.isDestroyed?.()) tileset.destroy()
                return
            }
            pointCloudTileset = tileset
            // 点云观感：适度衰减与屏幕空间误差
            if (tileset.pointCloudShading) {
                tileset.pointCloudShading.attenuation = true
                tileset.pointCloudShading.maximumAttenuation = 2
            }
            tileset.maximumScreenSpaceError = 2
            props.viewer.scene.primitives.add(tileset)
        } catch (e) {
            console.warn('[gengcundou-bridge-layer] 点云 3D Tiles 加载失败', e)
        }
    },
    { immediate: true }
)

onUnmounted(() => {
    pointCloudLoadToken++
    removePointCloudTileset()
})
</script>

<template>
    <vc-entity
        :position="[BRIDGE_LNG, BRIDGE_LAT, BRIDGE_H]"
        :orientation="bridgeOrientation()"
    >
        <vc-graphics-model :uri="MODEL_URI" :minimumPixelSize="MODEL_MIN_PIXEL" />
    </vc-entity>
</template>
